import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import partition from 'lodash/partition';

import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {Segment} from 'common/widgets/query-block/segment-menu/types';
import {
  bookmarkGroupByToGroupBy,
  groupByToBookmarkGroupBy,
  isGroupByEqual,
} from 'common/widgets/query-block/segment-menu/util';
import {FiltersOperator, QueryEventEntry} from 'common/widgets/query-entry-section/types';
import {bookmarkFilterByCohortToCohortFilters, getFilterByCohorts} from 'common/widgets/query-block/filter-menu/util';
import {SerializePropFilterContext} from 'common/report/util/arb';

import {FunnelBookmark} from './types';
import {
  mapBookmarkExclusionStepToEventEntry,
  mapBookmarkStepToEventEntry,
  mapEventEntryToBookmarkExclusionStep,
  mapEventEntryToBookmarkStep,
} from './format';

// TODO: This file should mostly be ignored for now. It was added for the Flows query builder project
// in order to allow Flows to be unblocked as Funnels underwent some changes and is only a subset of what is needed in the full funnels app.
// We will be merging code soon to avoid duplicated code and any confusion.
// Update (7/8/20): still TODO (andrew, bruna)

export const DEFAULT_VALUES: Partial<FunnelsBookmark> = Object.freeze({
  cohortFilters: [],
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  eventFilters: [],
  events: [],
  exclusionSteps: [],
  groupBy: [],
  holdingPropertyConstant: [],
  projectId: -1,
  querySampling: false,
  userFilters: [],
  utcOffset: 0,
});

/** Instantiates a PropertyFilter class from attributes */
const constructPropertyFilters = (filterPrimitives) => filterPrimitives.map((filter) => new PropertyFilter(filter));

/**
 * Determine if two lists of filters are equivalent
 */
const areAllFiltersEqual = (filterListA: PropertyFilter[], filterListB: PropertyFilter[], _: boolean): boolean =>
  filterListA.length === filterListB.length && filterListA.every((filter, index) => filter.equals(filterListB[index]));

/**
 * Determine if two event entries are eqivalent
 * @param {boolean} forQuery If it results in a difference in the query & results.
 */
const eventOmitProperties = (forQuery) => [`name`, ...(forQuery ? [`renamed`] : [])];
const areEventsEqual = (eventA: QueryEventEntry, eventB: QueryEventEntry, forQuery: boolean): boolean =>
  eventA &&
  eventB &&
  (eventA.filters.length < 2 || eventA.filtersOperator === eventB.filtersOperator) &&
  (eventA.entryIndexLabelToggle || false) === (eventB.entryIndexLabelToggle || false) &&
  isEqual(omit(eventA.event, eventOmitProperties(forQuery)), omit(eventB.event, eventOmitProperties(forQuery))) &&
  areAllFiltersEqual(eventA.filters, eventB.filters, forQuery);

const deepEquals = (itemA, itemB, _: boolean): boolean => isEqual(itemA, itemB);
const EQUIVALENCY_EVALUATORS: {[key: string]: (a: any, b: any, forQuery: boolean) => boolean} = Object.freeze({
  cohortFilters: areAllFiltersEqual,
  conversionWindow: deepEquals,
  countType: (a, b, _) => a === b,
  // dataGroupId equality can collapse to isEqual if we make sure it is never an empty string
  dataGroupId: (a, b, _) => (!a && !b) || a === b,
  dateRange: (a, b, _) => {
    if (!(a && b)) {
      return a === b;
    } // bail out if one is false-y
    return a.equals(b);
  },
  eventFilters: areAllFiltersEqual,
  events: (a, b, forQuery) =>
    a.length === b.length && a.every((entry, eventIndex) => areEventsEqual(entry, b[eventIndex], forQuery)),
  exclusionSteps: (a, b, forQuery) =>
    a.length === b.length &&
    a.every(
      (entry, eventIndex) =>
        areEventsEqual(entry, b[eventIndex], forQuery) &&
        entry.excludeAll === b[eventIndex].excludeAll &&
        isEqual(entry.excludeRange, b[eventIndex].excludeRange),
    ),
  groupBy: isGroupByEqual,
  controlGroup: (a, b, _) => isEqual(a, b),
  holdingPropertyConstant: deepEquals,
  userFilters: areAllFiltersEqual,
  timeBuckets: deepEquals,
  trendUnit: (a, b, _) => a === b,
  funnelOrder: (a, b, _) => a === b,
  /** querySampling is not saved and only affects query results */
  querySampling: (a, b, forQuery) => (forQuery ? a === b : true),
  chartType: (a, b) => a === b,
  eventIndex: (a, b, forQuery) => forQuery || a === b,
});

export class FunnelsBookmark {
  /**
   * Convert from bookmark to funnel query, reverse of "generateBookmark"
   * @param params bookmark params
   * @param customEvents ist of supported custom events.
   */
  public static fromBookmark(params) {
    const attrs = FunnelsBookmark.bookmarkToClassParams(params);
    return new FunnelsBookmark(attrs);
  }

  public static bookmarkToClassParams(params) {
    const attrs: Partial<FunnelsBookmark> = {};

    if (params.steps) {
      attrs.events = params.steps.map(mapBookmarkStepToEventEntry.bind(null, []));
    }
    if (params.exclusions) {
      attrs.exclusionSteps = params.exclusions.map(
        mapBookmarkExclusionStepToEventEntry.bind(null, attrs.events.length, []),
      );
    }

    if (params.group_by) {
      attrs.groupBy = bookmarkGroupByToGroupBy(params.group_by);
    }

    // TODO: Split this into cohortFilter and userFilters when we switch over to the new API
    attrs.cohortFilters = bookmarkFilterByCohortToCohortFilters(params.filter_by_cohort);

    if (params.date_range) {
      attrs.dateRange = FullLengthDateRange.fromBookmarkFormat(params.date_range).getAttributes();
    }

    if (params.filter_by_event) {
      const filters = partition(params.filter_by_event.children, `event`);
      attrs.eventFilters = filters[0].map((filter) => filter.event);
      attrs.userFilters = filters[1].map((filter) => filter.user);
    }

    if (params.data_group_id) {
      attrs.dataGroupId = params.data_group_id;
    }

    return attrs;
  }

  /**
   * Parses filter by cohorts
   * Definition moved to `iron/common/widgets/query-block/filter-menu/util.ts`.
   */
  public version: number;
  public dataGroupId: string;
  public dateRange: Partial<FullLengthDateRange>;
  public cohortFilters: PropertyFilter[];
  public eventFilters: PropertyFilter[];
  public events: QueryEventEntry[];
  public exclusionSteps: QueryEventEntry[];
  public groupBy: Segment[];

  /** transient, should not be persisted */
  public querySampling: boolean;
  public userFilters: PropertyFilter[];

  /**
   * @param [attrs] - Attributes to set on the new FunnelsBookmark object
   */
  constructor(attrs: Partial<FunnelsBookmark> = {}) {
    Object.assign(this, DEFAULT_VALUES, attrs);

    // Instantiate models from primitive attrs
    this.dateRange = new FullLengthDateRange(this.dateRange);
    this.events = this.events.map((entry) => ({...entry, filters: constructPropertyFilters(entry.filters)}));
    this.exclusionSteps = this.exclusionSteps.map((entry) => ({
      ...entry,
      filters: constructPropertyFilters(entry.filters),
    }));

    // force filter initialization
    // eslint-disable-next-line no-self-assign
    this.filters = this.filters;
  }

  get eventSelection() {
    return this.events.filter(Boolean);
  }

  get filters(): PropertyFilter[] {
    return [...this.eventFilters, ...this.userFilters, ...this.cohortFilters];
  }

  set filters(filters) {
    this.eventFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Event));
    this.userFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.User));
    this.cohortFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Cohort));
  }

  /**
   * Determine if this funnels query is equal to another funnels query
   * @param otherQuery The other query to compare this query to
   * @param forQuery
   */
  public equals(otherQuery: FunnelsBookmark, forQuery = false) {
    const evaluators = Object.keys(EQUIVALENCY_EVALUATORS).filter(
      (attr) =>
        Object.prototype.hasOwnProperty.call(this, attr) && Object.prototype.hasOwnProperty.call(otherQuery, attr),
    );

    return evaluators.every((attr) => EQUIVALENCY_EVALUATORS[attr](this[attr], otherQuery[attr], forQuery));
  }

  /**
   * Determine if this funnels query is equal to another funnels query in terms of requiring a re-query.
   * (i.e. will it generate a query that will return different results)
   * @param otherQuery The other query to compare this query to
   */
  public generatedQueryEquals(otherQuery) {
    return this.equals(otherQuery, true);
  }

  /**
   * Generate a bookmark object that can make a query and be persisted as a saved report
   */
  public generateBookmark({includeNegatedCohort = true} = {}): Partial<FunnelBookmark> {
    const steps = this.getEventsAsBookmark();
    const exclusions = this.getExclusionStepsAsBookmark();
    const groupBy = groupByToBookmarkGroupBy(this.groupBy, includeNegatedCohort);

    const params: Partial<FunnelBookmark> = {
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
      steps,
      exclusions,
      date_range: this.dateRange.getBookmarkFormat(),
      filter_by_cohort: JSON.stringify(this.cohortFilters.length ? getFilterByCohorts(this.cohortFilters) : {}),
      version: this.version,
    };

    if (!isEmpty(groupBy)) {
      params.group_by = groupBy;
    }

    // TODO: Remove userFilters length check once we switch to the new API
    if (this.eventFilters.length > 0 || this.userFilters.length > 0) {
      params.filter_by_event = this.getFilterByEvent();
    }

    return params;
  }

  public getFilterByEvent() {
    // TODO: Remove userFilter merge from this once we switch over to the new API
    return {
      operator: FiltersOperator.And,
      children: [
        ...this.eventFilters.map((filter) => ({event: filter})),
        ...this.userFilters.map((filter) => ({user: filter})),
      ],
    };
  }

  private getEventsAsBookmark() {
    return this.events.map(mapEventEntryToBookmarkStep.bind(null, this.getPropertySerializerConfig()));
  }

  private getExclusionStepsAsBookmark() {
    return this.exclusionSteps.map(
      mapEventEntryToBookmarkExclusionStep.bind(null, this.events.length, this.getPropertySerializerConfig()),
    );
  }

  /** return properties necessary for arb-selector conversion of property filters */
  private getPropertySerializerConfig(): SerializePropFilterContext {
    return {
      topLevelDataGroupId: this.dataGroupId,
      projectId: 0, // TODO
      utcOffset: 0, // TODO
    };
  }
}
